/**
 * Recent View Cache
 *
 * Browser-side localStorage cache that tracks the most recently used (MRU)
 * saved view per entity type per user. When a user revisits a list view
 * without an explicit `?view=` query param, the cached view ID is used to
 * automatically restore their previous selection.
 *
 * ## Storage Format
 *
 * Key:   `rws_recent_saved_view:<userId>:<entityType>`
 * Value: A saved-view UUID **or** the sentinel `__none__` (see below).
 *
 * ## Design Decisions
 *
 * 1. **User-scoped keys** — Prevents cached-view bleed across accounts on
 *    shared browsers (e.g. a kiosk). Falls back to `'anonymous'` when no
 *    user context is available.
 *
 * 2. **`__none__` sentinel** — When a user explicitly clears the active view
 *    (clicks "All Properties"), we store `__none__` so that the next visit
 *    does NOT re-apply an old cached view. This distinguishes "user has never
 *    selected a view" (key absent → no auto-restore) from "user deliberately
 *    chose unfiltered" (key = `__none__` → stay unfiltered).
 *
 * 3. **Graceful degradation** — If `localStorage` is unavailable (private
 *    browsing, storage full, etc.) all operations silently no-op so the
 *    feature never breaks the application.
 */

import type { SavedViewEntityType } from '../api/types'

// ─── Constants ──────────────────────────────────────────────────────

/** localStorage key prefix */
const STORAGE_PREFIX = 'rws_recent_saved_view'

/**
 * Sentinel value stored when the user explicitly clears the active view.
 * Distinguishes "never selected" (key missing) from "deliberately cleared".
 */
export const NONE_SENTINEL = '__none__'

// ─── Helpers ────────────────────────────────────────────────────────

/**
 * Build the localStorage key for a given entity type and user.
 *
 * @param entityType - The saved-view entity type (e.g. `'property'`).
 * @param userId     - Current user ID. Falls back to `'anonymous'`.
 * @returns Fully qualified localStorage key.
 *
 * @example
 * buildKey('property', 'usr-123') // → "rws_recent_saved_view:usr-123:property"
 * buildKey('tenant')              // → "rws_recent_saved_view:anonymous:tenant"
 */
export function buildKey(entityType: SavedViewEntityType, userId?: string): string {
    return `${STORAGE_PREFIX}:${userId || 'anonymous'}:${entityType}`
}

// ─── Public API ─────────────────────────────────────────────────────

/**
 * Get the most recently used saved-view ID for an entity type.
 *
 * @returns The view UUID, `'__none__'` if the user explicitly cleared,
 *          or `null` if no cached value exists.
 */
export function getRecentViewId(entityType: SavedViewEntityType, userId?: string): string | null {
    try {
        return localStorage.getItem(buildKey(entityType, userId))
    } catch {
        // localStorage unavailable (e.g. private browsing)
        return null
    }
}

/**
 * Cache the most recently selected saved-view ID.
 *
 * @param entityType - The entity type the view belongs to.
 * @param viewId     - The saved-view UUID to cache.
 * @param userId     - Current user ID for key scoping.
 */
export function setRecentViewId(entityType: SavedViewEntityType, viewId: string, userId?: string): void {
    try {
        localStorage.setItem(buildKey(entityType, userId), viewId)
    } catch {
        // Storage full or unavailable — silently ignore
    }
}

/**
 * Record that the user explicitly cleared the active view (chose "All …").
 *
 * Stores the `__none__` sentinel so subsequent visits stay unfiltered
 * rather than re-applying a previously cached view.
 */
export function clearRecentViewId(entityType: SavedViewEntityType, userId?: string): void {
    try {
        localStorage.setItem(buildKey(entityType, userId), NONE_SENTINEL)
    } catch {
        // Storage full or unavailable — silently ignore
    }
}

/**
 * Completely remove the cached entry for an entity type.
 *
 * Used when a cached view is deleted so the next visit falls through
 * to default behavior rather than attempting to restore a non-existent view.
 */
export function removeRecentViewCache(entityType: SavedViewEntityType, userId?: string): void {
    try {
        localStorage.removeItem(buildKey(entityType, userId))
    } catch {
        // Storage unavailable — silently ignore
    }
}
